import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiDownload } from 'react-icons/fi';
import { fetchAudit, exportAuditCsv } from '../../utils/sentimentApi';

const EXECUTOR_BADGE = {
  ai:     'sm-badge-ai',
  human:  'sm-badge-human',
  system: 'sm-badge-system',
};
const STATUS_BADGE = {
  success: 'sm-badge-success',
  failed:  'sm-badge-failed',
};

export default function SentimentAudit() {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [page, setPage]       = useState(0);
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = 25;

  const [filters, setFilters] = useState({
    executor_type: '',
    action_type:   '',
    target_system: '',
    status:        '',
    days:          '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAudit({ ...filters, page: page + 1, page_size: PAGE_SIZE });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(0);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAuditCsv(filters.days || 30);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div>
      <div className="sm-section-header">
        <h2 className="sm-section-title">Audit Log ({total})</h2>
        <div className="sm-filters">
          <select className="sm-select" value={filters.executor_type} onChange={e => handleFilter('executor_type', e.target.value)}>
            <option value="">All Executors</option>
            <option value="ai">AI Agent</option>
            <option value="human">Human</option>
            <option value="system">System</option>
          </select>
          <select className="sm-select" value={filters.status} onChange={e => handleFilter('status', e.target.value)}>
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
          <select className="sm-select" value={filters.days} onChange={e => handleFilter('days', e.target.value)}>
            <option value="">All Time</option>
            <option value="1">Last 24h</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
          </select>
          <button className="sm-btn sm-btn-ghost" onClick={load} title="Refresh"><FiRefreshCw size={14} /></button>
          <button
            className="sm-btn sm-btn-outline"
            onClick={handleExport}
            disabled={exporting}
            title="Export CSV"
            style={{ gap: 6 }}
          >
            <FiDownload size={14} /> {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>
      )}

      {loading ? (
        <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading audit log…</span></div>
      ) : items.length === 0 ? (
        <div className="sm-empty"><div className="sm-empty-icon">📋</div><span>No audit records found.</span></div>
      ) : (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Target System</th>
                <th>Executed By</th>
                <th>Executor</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a, i) => (
                <tr key={a.action_id} style={{ '--row-delay': `${i * 25}ms` }}>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)', whiteSpace: 'nowrap' }}>{fmtDate(a.executed_at)}</td>
                  <td style={{ fontWeight: 600, fontSize: 13 }}>{a.action_type}</td>
                  <td>
                    <span style={{ fontSize: 12, background: 'var(--sm-bg)', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace' }}>
                      {a.target_system}
                    </span>
                  </td>
                  <td style={{ fontSize: 13 }}>{a.executed_by}</td>
                  <td>
                    <span className={`sm-badge ${EXECUTOR_BADGE[a.executor_type] || 'sm-badge-system'}`}>
                      {a.executor_type}
                    </span>
                  </td>
                  <td>
                    <span className={`sm-badge ${STATUS_BADGE[a.status] || ''}`}>{a.status}</span>
                    {a.error_message && (
                      <div style={{ fontSize: 11, color: 'var(--sm-red)', marginTop: 2 }}>{a.error_message}</div>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)', maxWidth: 220 }}>{a.action_detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="sm-pagination">
        <span>{total} total records</span>
        <button className="sm-btn sm-btn-ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
        <span>Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
        <button className="sm-btn sm-btn-ghost" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
      </div>
    </div>
  );
}
